import { useSessionStorage } from '@uidotdev/usehooks';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VIEW_PARAM_KEY } from 'utils/viewParam';

type ViewStore = Record<string, string>;

/**
 * The active view for one list surface, read from the address first and the browser session second.
 *
 * The two layers answer different questions. The URL carries the view into a shared link and back
 * through history. The session store is what survives arriving at the page by a route that carries
 * no query at all — the left-hand menu — which the address alone cannot do. `sessionStorage` rather
 * than `localStorage` on purpose: the choice has to outlive leaving the page, but a view picked in
 * March must not still be selected in June.
 *
 * A value this surface does not offer falls back to `fallback` instead of selecting nothing.
 *
 * `mutate` lets a caller change other params in the same navigation — two `setSearchParams` calls in
 * one handler would both derive from the current render's location, so the second would drop the first.
 */
export function useViewParam<T extends string>(
    surfaceKey: string,
    options: readonly T[],
    fallback: T
): [T, (next: T, mutate?: (params: URLSearchParams) => void) => void] {
    const [searchParams, setSearchParams] = useSearchParams();
    const [store, setStore] = useSessionStorage<ViewStore>('list-view', {});

    const offered = (raw: string | null | undefined): T | null =>
        raw && (options as readonly string[]).includes(raw) ? (raw as T) : null;
    const view = offered(searchParams.get(VIEW_PARAM_KEY)) ?? offered(store?.[surfaceKey]) ?? fallback;

    const setView = useCallback(
        (next: T, mutate?: (params: URLSearchParams) => void) => {
            setStore((previous) => ({ ...previous, [surfaceKey]: next }));
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set(VIEW_PARAM_KEY, next);
                    mutate?.(params);
                    return params;
                },
                { replace: true }
            );
        },
        [surfaceKey, setStore, setSearchParams]
    );

    // Put the resolved view in the address whenever it is not already there — arriving by a route
    // that carries no query is exactly the case the session store covers, and the address should
    // still say which view is on screen so it can be read, copied and shared.
    // Built from the live address, not `searchParams`: a list rendered below restores its remembered
    // filter on mount, React flushes that child effect first, and both this snapshot and the
    // functional form's `prev` still hold the pre-navigation location, so either would drop it.
    useEffect(() => {
        if (searchParams.get(VIEW_PARAM_KEY) === view) return;
        setSearchParams(
            () => {
                const params = new URLSearchParams(window.location.search);
                params.set(VIEW_PARAM_KEY, view);
                return params;
            },
            { replace: true }
        );
    }, [view, searchParams, setSearchParams]);

    return [view, setView];
}
